/**
 * Admin Map Tracer Page
 * Tool for tracing unit polygons on the site plan
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { MapCanvas } from '@/components/admin/map-tracer/MapCanvas';
import { UnitSelector } from '@/components/admin/map-tracer/UnitSelector';
import { TracingControls } from '@/components/admin/map-tracer/TracingControls';
import { CoordinatePanel } from '@/components/admin/map-tracer/CoordinatePanel';
import { Trash2, CheckCircle2, Eye, EyeOff, MoreVertical } from 'lucide-react';
import {
  getUnitsWithMapCoordinates,
  updateUnitMapCoordinates,
  updateUnitMapCoordinatesByNumber,
  createSiteMapPolygon,
  updateSiteMapPolygon,
  getSiteMapPolygons,
  deleteSiteMapPolygon,
  assignUnitsToSiteMapPolygon,
  getUnitsBySiteMapPolygon,
  type SiteMapPolygon,
} from '@/lib/admin/map-coordinates';
import { getAllUnitsForAdmin } from '@/lib/admin/units';
import type { MapCoordinates } from '@/types/map';
import { Save, Loader2, MapPin, Link } from 'lucide-react';
import { trackEvent } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface PolygonHistory {
  points: number[][];
}

type TracerMode = 'create-polygon' | 'assign-units';

export default function AdminMapTracer() {
  const { toast } = useToast();
  const [mode, setMode] = useState<TracerMode>('create-polygon');
  
  // Site Map Polygon state (for create-polygon mode)
  const [selectedBlock, setSelectedBlock] = useState<'A' | 'B'>('A');
  const [selectedPolygonNumber, setSelectedPolygonNumber] = useState<number>(1);
  const [selectedSiteMapPolygon, setSelectedSiteMapPolygon] = useState<SiteMapPolygon | null>(null);
  const [siteMapPolygons, setSiteMapPolygons] = useState<SiteMapPolygon[]>([]);
  
  // Unit assignment state (for assign-units mode)
  const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [selectedUnitFloor, setSelectedUnitFloor] = useState<string | null>(null);
  const [selectedUnitsForAssignment, setSelectedUnitsForAssignment] = useState<Set<string>>(new Set());
  const [assignedUnits, setAssignedUnits] = useState<Map<string, Set<string>>>(new Map()); // polygonId -> Set of unit numbers
  
  const [currentPolygon, setCurrentPolygon] = useState<number[][]>([]);
  const [history, setHistory] = useState<PolygonHistory[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [existingPolygons, setExistingPolygons] = useState<
    Map<string, { points: number[][]; color?: string }>
  >(new Map());
  const [unitsWithCoordinates, setUnitsWithCoordinates] = useState<Set<string>>(new Set());
  const [allUnits, setAllUnits] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [showGrid, setShowGrid] = useState(false);
  const [hiddenPolygons, setHiddenPolygons] = useState<Set<string>>(new Set());
  const [allUnitsData, setAllUnitsData] = useState<Map<string, { id: string; floor: string }[]>>(new Map());

  // Load existing polygons on mount
  useEffect(() => {
    loadExistingPolygons();
    loadAllUnits();
    loadSiteMapPolygons();
  }, []);

  // Load polygon based on mode
  useEffect(() => {
    if (mode === 'create-polygon') {
      // Load site map polygon if selected
      if (selectedSiteMapPolygon) {
        const coords = selectedSiteMapPolygon.map_coordinates;
        if (coords?.type === 'Polygon' && coords.points && coords.points.length >= 3) {
          setCurrentPolygon(coords.points);
          setHistory([{ points: [...coords.points] }]);
          setHistoryIndex(0);
        } else {
          setCurrentPolygon([]);
          setHistory([]);
          setHistoryIndex(-1);
        }
      } else {
        setCurrentPolygon([]);
        setHistory([]);
        setHistoryIndex(-1);
      }
    } else {
      // Legacy mode: Load polygon for selected unit
      if (selectedUnit && selectedUnitFloor) {
        const key = `${selectedUnit}-${selectedUnitFloor}`;
        let polygon = existingPolygons.get(key);
        
        // If polygon not found with floor-specific key, try to find any polygon for this unit number
        // This handles legacy polygons saved before floor-specific changes
        if (!polygon) {
          const unitsForNumber = allUnitsData.get(selectedUnit);
          if (unitsForNumber) {
            // Try to find polygon on any floor of this unit
            for (const unit of unitsForNumber) {
              const legacyKey = `${selectedUnit}-${unit.floor}`;
              const legacyPolygon = existingPolygons.get(legacyKey);
              if (legacyPolygon) {
                polygon = legacyPolygon;
                // Update selected floor to match the found polygon
                if (unit.floor !== selectedUnitFloor) {
                  setSelectedUnitFloor(unit.floor);
                  const unitData = unitsForNumber.find(u => u.floor === unit.floor);
                  if (unitData) {
                    setSelectedUnitId(unitData.id);
                  }
                }
                break;
              }
            }
          }
        }
        
        if (polygon && polygon.points.length >= 3) {
          setCurrentPolygon(polygon.points);
          setHistory([{ points: [...polygon.points] }]);
          setHistoryIndex(0);
        } else {
          setCurrentPolygon([]);
          setHistory([]);
          setHistoryIndex(-1);
        }
      } else {
        setCurrentPolygon([]);
        setHistory([]);
        setHistoryIndex(-1);
      }
    }
  }, [mode, selectedSiteMapPolygon, selectedUnit, selectedUnitFloor, existingPolygons, allUnitsData]);

  const loadExistingPolygons = async () => {
    setLoading(true);
    try {
      const { data, error } = await getUnitsWithMapCoordinates();
      if (error) {
        toast({
          title: 'Error',
          description: error,
          variant: 'destructive',
        });
        return;
      }

      if (data) {
        const polygons = new Map<string, { points: number[][]; color?: string }>();
        const unitsWithCoords = new Set<string>();
        const unitsMap = new Map<string, { id: string; floor: string }[]>();

        data.forEach((unit) => {
          // Build map of unit numbers to their IDs and floors
          if (!unitsMap.has(unit.unit_number)) {
            unitsMap.set(unit.unit_number, []);
          }
          unitsMap.get(unit.unit_number)!.push({ id: unit.unit_id, floor: unit.floor });

          if (
            unit.map_coordinates?.type === 'Polygon' &&
            unit.map_coordinates.points &&
            Array.isArray(unit.map_coordinates.points) &&
            unit.map_coordinates.points.length >= 3
          ) {
            // Use unit_number-floor as key to handle multiple floors
            const key = `${unit.unit_number}-${unit.floor}`;
            polygons.set(key, {
              points: unit.map_coordinates.points,
              color: 'rgba(59, 130, 246, 0.2)',
            });
            unitsWithCoords.add(unit.unit_number);
          }
        });

        // Handle legacy polygons: if multiple floors have identical coordinates,
        // ensure all are marked as having coordinates
        const legacyPolygons = new Map<string, { points: number[][]; floors: string[] }>();
        polygons.forEach((polygon, key) => {
          const [unitNumber] = key.split('-');
          const matchingKey = Array.from(polygons.keys()).find(k => {
            if (k.startsWith(unitNumber + '-')) {
              const otherPolygon = polygons.get(k);
              return otherPolygon && 
                     otherPolygon.points.length === polygon.points.length &&
                     otherPolygon.points.every((p, i) => 
                       Math.abs(p[0] - polygon.points[i][0]) < 0.01 &&
                       Math.abs(p[1] - polygon.points[i][1]) < 0.01
                     );
            }
            return false;
          });
          
          if (matchingKey && matchingKey !== key) {
            // Found duplicate polygon (legacy case where same coordinates saved to multiple floors)
            const legacyKey = unitNumber;
            if (!legacyPolygons.has(legacyKey)) {
              legacyPolygons.set(legacyKey, { points: polygon.points, floors: [] });
            }
            legacyPolygons.get(legacyKey)!.floors.push(key.split('-')[1]);
          }
        });

        setAllUnitsData(unitsMap);

        console.log('Loaded polygons:', {
          total: data.length,
          withCoordinates: polygons.size,
          polygons: Array.from(polygons.keys()),
        });

        // Merge with existing polygons (don't replace, to preserve site map polygons)
        setExistingPolygons((prev) => {
          const merged = new Map(prev);
          polygons.forEach((value, key) => {
            merged.set(key, value);
          });
          console.log('Merged existing polygons with unit polygons, total keys:', Array.from(merged.keys()));
          return merged;
        });
        setUnitsWithCoordinates(unitsWithCoords);
      }
    } catch (error) {
      console.error('Error loading polygons:', error);
      toast({
        title: 'Error',
        description: 'Failed to load existing polygons',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAllUnits = async () => {
    try {
      const { data } = await getAllUnitsForAdmin();
      if (data) {
        // Store all unit numbers (for polygon list)
        const uniqueUnits = new Set<string>();
        data.forEach((unit) => {
          uniqueUnits.add(unit.unit_number);
        });
        setAllUnits(Array.from(uniqueUnits));
      }
    } catch (error) {
      console.error('Error loading units:', error);
    }
  };

  const loadSiteMapPolygons = async () => {
    try {
      console.log('Loading site map polygons...');
      const { data, error } = await getSiteMapPolygons();
      if (error) {
        console.error('Error loading site map polygons:', error);
        toast({
          title: 'Error',
          description: error,
          variant: 'destructive',
        });
        return;
      }
      console.log('Site map polygons loaded:', data?.length || 0, data);
      if (data) {
        setSiteMapPolygons(data);
        
        // Update existing polygons map to include site map polygons
        const polygonsMap = new Map<string, { points: number[][]; color?: string }>();
        data.forEach((polygon) => {
          if (
            polygon.map_coordinates?.type === 'Polygon' &&
            polygon.map_coordinates.points &&
            Array.isArray(polygon.map_coordinates.points) &&
            polygon.map_coordinates.points.length >= 3
          ) {
            const key = `${polygon.block}-${polygon.polygon_number}`;
            polygonsMap.set(key, {
              points: polygon.map_coordinates.points,
              color: 'rgba(59, 130, 246, 0.2)',
            });
            console.log('Added site map polygon to map:', key, polygon.block, polygon.polygon_number, polygon.map_coordinates.points.length, 'points');
          }
        });
        
        // Merge with existing polygons
        setExistingPolygons((prev) => {
          const merged = new Map(prev);
          polygonsMap.forEach((value, key) => {
            merged.set(key, value);
          });
          const blockAKeys = Array.from(merged.keys()).filter(k => k.startsWith('A-'));
          const blockBKeys = Array.from(merged.keys()).filter(k => k.startsWith('B-'));
          console.log('Merged polygons map size:', merged.size, 'Block A:', blockAKeys.length, 'Block B:', blockBKeys.length, 'All keys:', Array.from(merged.keys()));
          return merged;
        });
      } else {
        console.log('No site map polygons data returned');
      }
    } catch (error) {
      console.error('Error loading site map polygons:', error);
    }
  };

  const handlePointAdd = useCallback(
    (point: [number, number], insertIndex?: number) => {
      console.log('handlePointAdd called:', { point, insertIndex, mode, selectedUnit, selectedSiteMapPolygon, currentPolygonLength: currentPolygon.length });
      
      // In create-polygon mode, we don't need a selectedUnit
      // In assign-units mode, we need a selectedUnit
      if (mode === 'assign-units' && !selectedUnit) {
        console.warn('No unit selected, showing toast');
        toast({
          title: 'No Unit Selected',
          description: 'Please select a unit first',
          variant: 'destructive',
        });
        return;
      }

      if (!point || point.length !== 2 || isNaN(point[0]) || isNaN(point[1])) {
        console.error('Invalid point received:', point);
        toast({
          title: 'Invalid Point',
          description: 'Could not add point - invalid coordinates',
          variant: 'destructive',
        });
        return;
      }

      console.log('Adding point:', point, 'to unit:', selectedUnit, 'at index:', insertIndex, 'current polygon:', currentPolygon);

      // Insert at specific index if provided, otherwise add to end
      const newPolygon = insertIndex !== undefined && insertIndex >= 0 && insertIndex <= currentPolygon.length
        ? [...currentPolygon.slice(0, insertIndex), point, ...currentPolygon.slice(insertIndex)]
        : [...currentPolygon, point];
      
      console.log('New polygon:', newPolygon);
      setCurrentPolygon(newPolygon);

      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ points: [...newPolygon] });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);

      console.log('Point added successfully, new polygon length:', newPolygon.length);

      trackEvent('map_tracer_point_added', {
        unit_number: selectedUnit || selectedSiteMapPolygon?.block + '-' + selectedSiteMapPolygon?.polygon_number || 'unknown',
        point_count: newPolygon.length,
      });
    },
    [mode, selectedUnit, selectedSiteMapPolygon, currentPolygon, history, historyIndex, toast]
  );

  const handlePointDelete = useCallback(
    (index: number) => {
      // In create-polygon mode, we don't need selectedUnit
      // In assign-units mode, we need selectedUnit
      if (mode === 'assign-units' && !selectedUnit) return;
      if (currentPolygon.length <= 3) {
        toast({
          title: 'Cannot Delete',
          description: 'A polygon needs at least 3 points',
          variant: 'destructive',
        });
        return;
      }

      const newPolygon = currentPolygon.filter((_, i) => i !== index);
      setCurrentPolygon(newPolygon);

      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ points: [...newPolygon] });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);

      trackEvent('map_tracer_point_deleted', {
        unit_number: selectedUnit || selectedSiteMapPolygon?.block + '-' + selectedSiteMapPolygon?.polygon_number || 'unknown',
        point_count: newPolygon.length,
      });
    },
    [mode, selectedUnit, selectedSiteMapPolygon, currentPolygon, history, historyIndex, toast]
  );

  const handlePointMove = useCallback(
    (index: number, point: [number, number]) => {
      // In create-polygon mode, we don't need selectedUnit
      // In assign-units mode, we need selectedUnit
      if (mode === 'assign-units' && !selectedUnit) return;
      if (currentPolygon.length === 0 || index < 0 || index >= currentPolygon.length) return;

      const newPolygon = [...currentPolygon];
      newPolygon[index] = point;
      setCurrentPolygon(newPolygon);

      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ points: [...newPolygon] });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [mode, selectedUnit, currentPolygon, history, historyIndex]
  );

  const polygonStartRef = useRef<number[][] | null>(null);

  const handlePolygonMoveStart = useCallback(() => {
    // Store initial polygon position when starting to drag
    polygonStartRef.current = currentPolygon.map((p) => [p[0], p[1]]);
  }, [currentPolygon]);

  const handlePolygonMove = useCallback(
    (deltaX: number, deltaY: number) => {
      // In create-polygon mode, we don't need selectedUnit
      // In assign-units mode, we need selectedUnit
      if (mode === 'assign-units' && !selectedUnit) return;
      if (currentPolygon.length === 0) return;

      // Store initial polygon on first move if not already stored
      if (!polygonStartRef.current) {
        polygonStartRef.current = currentPolygon.map((p) => [p[0], p[1]]);
      }

      // Apply delta to initial polygon position
      const newPolygon = polygonStartRef.current.map((point) => [
        point[0] + deltaX,
        point[1] + deltaY,
      ]) as number[][];
      setCurrentPolygon(newPolygon);

      // Update history
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push({ points: [...newPolygon] });
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    },
    [mode, selectedUnit, currentPolygon, history, historyIndex]
  );

  // Reset polygon start ref when polygon changes or selection changes
  useEffect(() => {
    polygonStartRef.current = null;
  }, [selectedUnit, selectedSiteMapPolygon]);

  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setCurrentPolygon([...history[newIndex].points]);
    }
  }, [history, historyIndex]);

  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setCurrentPolygon([...history[newIndex].points]);
    }
  }, [history, historyIndex]);

  const handleClear = useCallback(() => {
    setCurrentPolygon([]);
    setHistory([]);
    setHistoryIndex(-1);
  }, []);

  // Helper function to check if a unit is a townhouse (has multiple floors)
  const isTownhouse = useCallback((unitNumber: string): boolean => {
    const unitsForNumber = allUnitsData.get(unitNumber);
    return unitsForNumber ? unitsForNumber.length > 1 : false;
  }, [allUnitsData]);

  // Helper function to get all floors for a unit number
  const getUnitFloors = useCallback((unitNumber: string): string[] => {
    const unitsForNumber = allUnitsData.get(unitNumber);
    return unitsForNumber ? unitsForNumber.map(u => u.floor) : [];
  }, [allUnitsData]);

  const handleSave = async () => {
    if (currentPolygon.length < 3) {
      toast({
        title: 'Invalid Polygon',
        description: 'A polygon needs at least 3 points',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const coordinates: MapCoordinates = {
        type: 'Polygon',
        points: currentPolygon,
      };

      if (mode === 'create-polygon') {
        // Save as site map polygon
        if (!selectedBlock || !selectedPolygonNumber) {
          toast({
            title: 'Missing Information',
            description: 'Please select block and polygon number',
            variant: 'destructive',
          });
          setSaving(false);
          return;
        }

        let result;
        if (selectedSiteMapPolygon) {
          // Update existing site map polygon
          result = await updateSiteMapPolygon(selectedSiteMapPolygon.id, coordinates);
        } else {
          // Create new site map polygon
          result = await createSiteMapPolygon(selectedBlock, selectedPolygonNumber, coordinates);
        }

        if (result.success && result.data) {
          // Reload site map polygons to get the latest data
          await loadSiteMapPolygons();
          
          // Update selectedSiteMapPolygon to the saved polygon so it remains editable
          // This ensures the polygon stays in currentPolygon and can be moved
          setSelectedSiteMapPolygon(result.data);
          
          // Update currentPolygon to match the saved data (in case of any rounding differences)
          const coords = result.data.map_coordinates;
          if (coords?.type === 'Polygon' && coords.points && coords.points.length >= 3) {
            setCurrentPolygon(coords.points);
            setHistory([{ points: [...coords.points] }]);
            setHistoryIndex(0);
          }
          
          toast({
            title: 'Saved',
            description: `Site map polygon ${selectedBlock}-${selectedPolygonNumber} saved`,
          });
        } else {
          toast({
            title: 'Save Failed',
            description: result.error || 'Unknown error',
            variant: 'destructive',
          });
        }
      } else {
        // Legacy mode: Save to unit (keeping for backward compatibility)
        if (!selectedUnit) {
          toast({
            title: 'No Unit Selected',
            description: 'Please select a unit first',
            variant: 'destructive',
          });
          setSaving(false);
          return;
        }

        // If selectedUnitId is missing, try to find it from allUnitsData
        let unitIdToUse = selectedUnitId;
        if (!unitIdToUse && selectedUnit && selectedUnitFloor) {
          const unitsForNumber = allUnitsData.get(selectedUnit);
          if (unitsForNumber) {
            const unit = unitsForNumber.find(u => u.floor === selectedUnitFloor) || unitsForNumber[0];
            if (unit) {
              unitIdToUse = unit.id;
            }
          }
        }

        if (!unitIdToUse) {
          toast({
            title: 'Missing Unit ID',
            description: 'Unable to determine unit ID. Please reselect the unit.',
            variant: 'destructive',
          });
          setSaving(false);
          return;
        }

        // Check if this is a townhouse (has multiple floors)
        const townhouse = isTownhouse(selectedUnit);
        
        let result;
        let floorsUpdated: string[] = [];

        if (townhouse) {
          // Townhouse: Save to ALL floors of this unit number
          const floors = getUnitFloors(selectedUnit);
          result = await updateUnitMapCoordinatesByNumber(selectedUnit, coordinates, selectedUnit);
          floorsUpdated = floors;
        } else {
          // Single unit: Save to specific unit ID (floor-specific)
          result = await updateUnitMapCoordinates(unitIdToUse, coordinates, selectedUnit);
          floorsUpdated = selectedUnitFloor ? [selectedUnitFloor] : [];
        }

        if (result.success) {
          // Reload polygons from database to ensure consistency
          await loadExistingPolygons();

          // Create success message based on whether it's a townhouse
          let description: string;
          if (townhouse) {
            const floorsStr = floorsUpdated.join(', ');
            description = `Polygon saved for unit ${selectedUnit} (all floors: ${floorsStr})`;
          } else {
            description = `Polygon saved for unit ${selectedUnit} (${selectedUnitFloor || 'Unknown Floor'})`;
          }

          toast({
            title: 'Saved',
            description,
          });
        } else {
          toast({
            title: 'Save Failed',
            description: result.error || 'Unknown error',
            variant: 'destructive',
          });
        }
      }
    } catch (error) {
      console.error('Error saving polygon:', error);
      toast({
        title: 'Save Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignUnits = async () => {
    if (!selectedSiteMapPolygon || selectedUnitsForAssignment.size === 0) {
      toast({
        title: 'Missing Information',
        description: 'Please select a site map polygon and at least one unit',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      // Get unit IDs from selected unit numbers
      const unitIds: string[] = [];
      selectedUnitsForAssignment.forEach((unitNumber) => {
        const unitsForNumber = allUnitsData.get(unitNumber);
        if (unitsForNumber) {
          // Assign all floors of this unit to the polygon
          unitsForNumber.forEach((unit) => {
            unitIds.push(unit.id);
          });
        }
      });

      if (unitIds.length === 0) {
        toast({
          title: 'No Units Found',
          description: 'Unable to find unit IDs for selected units',
          variant: 'destructive',
        });
        setSaving(false);
        return;
      }

      const result = await assignUnitsToSiteMapPolygon(unitIds, selectedSiteMapPolygon.id);

      if (result.success) {
        // Update assigned units map
        setAssignedUnits(prev => {
          const newMap = new Map(prev);
          newMap.set(selectedSiteMapPolygon.id, new Set(selectedUnitsForAssignment));
          return newMap;
        });
        
        toast({
          title: 'Assigned',
          description: `Assigned ${result.updatedCount || 0} unit(s) to polygon ${selectedSiteMapPolygon.block}-${selectedSiteMapPolygon.polygon_number}`,
        });
        // Keep the selection so user can see what was assigned
        // setSelectedUnitsForAssignment(new Set());
      } else {
        toast({
          title: 'Assignment Failed',
          description: result.error || 'Unknown error',
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error assigning units:', error);
      toast({
        title: 'Assignment Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (unitNumber: string, floor?: string) => {
    if (!confirm(`Delete polygon for unit ${unitNumber}${floor ? ` (${floor})` : ''}?`)) return;

    try {
      // Find the unit ID for this unit number and floor
      const unitsForNumber = allUnitsData.get(unitNumber);
      if (!unitsForNumber || unitsForNumber.length === 0) {
        toast({
          title: 'Error',
          description: `Unit ${unitNumber} not found`,
          variant: 'destructive',
        });
        return;
      }

      // If floor specified, find that specific unit, otherwise delete all floors
      const unitsToDelete = floor 
        ? unitsForNumber.filter(u => u.floor === floor)
        : unitsForNumber;

      if (unitsToDelete.length === 0) {
        toast({
          title: 'Error',
          description: `No unit found for ${unitNumber}${floor ? ` on ${floor}` : ''}`,
          variant: 'destructive',
        });
        return;
      }

      // Delete coordinates for each unit
      const coordinates: MapCoordinates = {
        type: 'Polygon',
        points: [],
      };

      let successCount = 0;
      for (const unit of unitsToDelete) {
        const result = await updateUnitMapCoordinates(unit.id, coordinates);
        if (result.success) {
          successCount++;
        }
      }

      if (successCount > 0) {
        // Reload polygons from database
        await loadExistingPolygons();

        // Clear selection if deleted unit was selected
        if (selectedUnit === unitNumber && (!floor || selectedUnitFloor === floor)) {
          setCurrentPolygon([]);
          setHistory([]);
          setHistoryIndex(-1);
        }

        toast({
          title: 'Deleted',
          description: `Polygon deleted for unit ${unitNumber}${floor ? ` (${floor})` : ''}`,
        });
      }
    } catch (error) {
      console.error('Error deleting polygon:', error);
      toast({
        title: 'Delete Failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z * 1.2, 5));
  const handleZoomOut = () => setZoom((z) => Math.max(z / 1.2, 0.5));
  const handleZoomReset = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Build polygon list data from all units, showing floor information
  // Use consistent key format: "unitNumber-floor" (e.g., "A-01-GF")
  const polygonListData = Array.from(allUnitsData.entries()).flatMap(([unitNumber, units]) => {
    return units.map((unit) => {
      const key = `${unitNumber}-${unit.floor}`;
      const polygon = existingPolygons.get(key);
      return {
        unitNumber: unitNumber, // Keep as unit number for key consistency
        unitDisplayName: `${unitNumber} (${unit.floor})`, // Display name for UI
        unitKey: key, // Use consistent key format
        unitId: unit.id,
        floor: unit.floor,
        pointCount: polygon?.points.length || 0,
        hasCoordinates: !!polygon,
      };
    });
  }).sort((a, b) => {
    // Sort by unit number, then by floor
    if (a.unitNumber !== b.unitNumber) {
      return a.unitNumber.localeCompare(b.unitNumber);
    }
    const floorOrder = { 'GF': 0, 'second': 1, 'third': 2, 'fourth': 3 };
    return (floorOrder[a.floor as keyof typeof floorOrder] || 99) - (floorOrder[b.floor as keyof typeof floorOrder] || 99);
  });

  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Map Tracer</h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          Trace unit polygons on the site plan to create interactive map coordinates
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 lg:gap-6">
          {/* Left Sidebar */}
          <div className="lg:col-span-1 space-y-4 order-2 lg:order-1">
            {/* Mode Toggle */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Mode</CardTitle>
                <CardDescription className="text-xs">Select operation mode</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <Tabs value={mode} onValueChange={(v) => {
                  setMode(v as TracerMode);
                  // Clear selections when switching modes
                  setSelectedUnit(null);
                  setSelectedUnitId(null);
                  setSelectedUnitFloor(null);
                  setSelectedSiteMapPolygon(null);
                  setSelectedUnitsForAssignment(new Set());
                  setCurrentPolygon([]);
                  setHistory([]);
                  setHistoryIndex(-1);
                }}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="create-polygon" className="text-xs">
                      <MapPin className="h-3 w-3 mr-1" />
                      Create Polygon
                    </TabsTrigger>
                    <TabsTrigger value="assign-units" className="text-xs">
                      <Link className="h-3 w-3 mr-1" />
                      Assign Units
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </CardContent>
            </Card>

            {/* Mode-specific content */}
            {mode === 'create-polygon' ? (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Site Map Polygon</CardTitle>
                  <CardDescription className="text-xs">Create or edit site map polygons</CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="block-select">Block</Label>
                    <Select value={selectedBlock} onValueChange={(v) => setSelectedBlock(v as 'A' | 'B')}>
                      <SelectTrigger id="block-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="A">Block A (Polygons 1-11)</SelectItem>
                        <SelectItem value="B">Block B (Polygons 12-32)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="polygon-number-select">Polygon Number</Label>
                    <Select
                      value={selectedPolygonNumber.toString()}
                      onValueChange={(v) => setSelectedPolygonNumber(parseInt(v, 10))}
                    >
                      <SelectTrigger id="polygon-number-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {selectedBlock === 'A'
                          ? Array.from({ length: 11 }, (_, i) => i + 1).map((num) => (
                              <SelectItem key={num} value={num.toString()}>
                                Polygon {num}
                              </SelectItem>
                            ))
                          : Array.from({ length: 21 }, (_, i) => i + 12).map((num) => (
                              <SelectItem key={num} value={num.toString()}>
                                Polygon {num}
                              </SelectItem>
                            ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="existing-polygon-select">Existing Polygon (Optional)</Label>
                    <Select
                      value={selectedSiteMapPolygon?.id || 'new'}
                      onValueChange={(v) => {
                        if (v === 'new') {
                          setSelectedSiteMapPolygon(null);
                          setCurrentPolygon([]);
                          setHistory([]);
                          setHistoryIndex(-1);
                        } else {
                          const polygon = siteMapPolygons.find((p) => p.id === v);
                          setSelectedSiteMapPolygon(polygon || null);
                          if (polygon) {
                            setSelectedBlock(polygon.block);
                            setSelectedPolygonNumber(polygon.polygon_number);
                          }
                        }
                      }}
                    >
                      <SelectTrigger id="existing-polygon-select">
                        <SelectValue placeholder="Select to edit existing polygon" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">Create New Polygon</SelectItem>
                        {siteMapPolygons
                          .filter((p) => p.block === selectedBlock)
                          .map((polygon) => (
                            <SelectItem key={polygon.id} value={polygon.id}>
                              {polygon.block}-{polygon.polygon_number}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedSiteMapPolygon && (
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Editing: {selectedSiteMapPolygon.block}-{selectedSiteMapPolygon.polygon_number}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => {
                          setSelectedSiteMapPolygon(null);
                          setCurrentPolygon([]);
                          setHistory([]);
                          setHistoryIndex(-1);
                        }}
                      >
                        Clear
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg">Assign Units</CardTitle>
                  <CardDescription className="text-xs">Link units to site map polygons</CardDescription>
                </CardHeader>
                <CardContent className="pt-0 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="polygon-select">Site Map Polygon</Label>
                    <Select
                      value={selectedSiteMapPolygon?.id || ''}
                      onValueChange={async (v) => {
                        const polygon = siteMapPolygons.find((p) => p.id === v);
                        setSelectedSiteMapPolygon(polygon || null);
                        
                        // Load assigned units for this polygon
                        if (polygon) {
                          console.log('Loading assigned units for polygon:', polygon.id, polygon.block, polygon.polygon_number);
                          const { data: units, error } = await getUnitsBySiteMapPolygon(polygon.id);
                          if (error) {
                            console.error('Error loading assigned units:', error);
                          }
                          console.log('Assigned units loaded:', units?.length || 0, units);
                          if (units && units.length > 0) {
                            const unitNumbers = new Set(units.map(u => u.unit_number));
                            console.log('Unit numbers set:', Array.from(unitNumbers));
                            setAssignedUnits(prev => {
                              const newMap = new Map(prev);
                              newMap.set(polygon.id, unitNumbers);
                              return newMap;
                            });
                            // Pre-select assigned units
                            setSelectedUnitsForAssignment(unitNumbers);
                            console.log('Pre-selected units:', Array.from(unitNumbers));
                          } else {
                            console.log('No units assigned to this polygon');
                            setAssignedUnits(prev => {
                              const newMap = new Map(prev);
                              newMap.set(polygon.id, new Set());
                              return newMap;
                            });
                            setSelectedUnitsForAssignment(new Set());
                          }
                        } else {
                          setSelectedUnitsForAssignment(new Set());
                        }
                      }}
                    >
                      <SelectTrigger id="polygon-select">
                        <SelectValue placeholder="Select a polygon" />
                      </SelectTrigger>
                      <SelectContent>
                        {siteMapPolygons.map((polygon) => (
                          <SelectItem key={polygon.id} value={polygon.id}>
                            {polygon.block}-{polygon.polygon_number}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedSiteMapPolygon && (
                    <>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label>Select Units</Label>
                          {assignedUnits.get(selectedSiteMapPolygon.id) && assignedUnits.get(selectedSiteMapPolygon.id)!.size > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {assignedUnits.get(selectedSiteMapPolygon.id)!.size} assigned
                            </Badge>
                          )}
                        </div>
                        <div className="max-h-[300px] overflow-y-auto space-y-2 border rounded-md p-2">
                          {Array.from(allUnitsData.keys())
                            .sort()
                            .map((unitNumber) => {
                              const unitsForNumber = allUnitsData.get(unitNumber);
                              if (!unitsForNumber) return null;
                              const isAssigned = assignedUnits.get(selectedSiteMapPolygon.id)?.has(unitNumber) || false;
                              return (
                                <div key={unitNumber} className="space-y-1">
                                  <div className="flex items-center space-x-2">
                                    <Checkbox
                                      id={`unit-${unitNumber}`}
                                      checked={selectedUnitsForAssignment.has(unitNumber)}
                                      onCheckedChange={(checked) => {
                                        const newSet = new Set(selectedUnitsForAssignment);
                                        if (checked) {
                                          newSet.add(unitNumber);
                                        } else {
                                          newSet.delete(unitNumber);
                                        }
                                        setSelectedUnitsForAssignment(newSet);
                                      }}
                                    />
                                    <Label
                                      htmlFor={`unit-${unitNumber}`}
                                      className={cn(
                                        "text-sm font-medium cursor-pointer flex-1",
                                        isAssigned && "text-green-600"
                                      )}
                                    >
                                      {unitNumber}
                                      {isAssigned && (
                                        <CheckCircle2 className="h-3 w-3 inline ml-1 text-green-600" />
                                      )}
                                    </Label>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>

                      <Button
                        onClick={handleAssignUnits}
                        disabled={selectedUnitsForAssignment.size === 0 || saving}
                        className="w-full"
                      >
                        {saving ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Assigning...
                          </>
                        ) : (
                          <>
                            <Link className="h-4 w-4 mr-2" />
                            Assign {selectedUnitsForAssignment.size} Unit(s)
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Legacy Unit Selection - Removed from assign-units mode as it's not needed */}
            {/* The assign-units mode uses the "Assign Units" card instead */}

            {/* Site Map Polygons List - Only show in create-polygon mode */}
            {mode === 'create-polygon' && (
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-lg">Site Map Polygons</CardTitle>
                      <CardDescription className="text-xs">
                        {siteMapPolygons.length} of 32 polygons created
                        {hiddenPolygons.size > 0 && (
                          <span className="ml-1 text-muted-foreground">
                            ({siteMapPolygons.length - hiddenPolygons.size} visible)
                          </span>
                        )}
                      </CardDescription>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => setHiddenPolygons(new Set())}>
                          <Eye className="h-4 w-4 mr-2" />
                          Show All
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          // Get all polygon keys from existingPolygons (includes both site map and legacy unit polygons)
                          const allPolygonKeys = Array.from(existingPolygons.keys());
                          setHiddenPolygons(new Set(allPolygonKeys));
                        }}>
                          <EyeOff className="h-4 w-4 mr-2" />
                          Hide All
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => {
                          // Hide all polygons that start with "A-" (Block A site map polygons)
                          const blockAPolygons = Array.from(existingPolygons.keys())
                            .filter(key => key.startsWith('A-') && /^A-\d+$/.test(key));
                          const newHidden = new Set(hiddenPolygons);
                          blockAPolygons.forEach(key => newHidden.add(key));
                          setHiddenPolygons(newHidden);
                        }}>
                          <EyeOff className="h-4 w-4 mr-2" />
                          Hide Block A
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          // Hide all polygons that start with "B-" (Block B site map polygons)
                          const blockBPolygons = Array.from(existingPolygons.keys())
                            .filter(key => key.startsWith('B-') && /^B-\d+$/.test(key));
                          const newHidden = new Set(hiddenPolygons);
                          blockBPolygons.forEach(key => newHidden.add(key));
                          setHiddenPolygons(newHidden);
                        }}>
                          <EyeOff className="h-4 w-4 mr-2" />
                          Hide Block B
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1 max-h-[300px] sm:max-h-[400px] overflow-y-auto -mr-2 pr-2">
                    {siteMapPolygons.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No site map polygons created yet
                      </p>
                    ) : (
                      siteMapPolygons
                        .sort((a, b) => {
                          if (a.block !== b.block) return a.block.localeCompare(b.block);
                          return a.polygon_number - b.polygon_number;
                        })
                        .map((polygon) => {
                          const coords = polygon.map_coordinates as any;
                          const pointCount = coords?.points?.length || 0;
                          const isSelected = selectedSiteMapPolygon?.id === polygon.id;
                          const polygonKey = `${polygon.block}-${polygon.polygon_number}`;
                          
                          return (
                            <div
                              key={polygon.id}
                              className={cn(
                                'flex items-center justify-between p-2 rounded-md hover:bg-muted cursor-pointer transition-colors',
                                isSelected && 'bg-muted border border-primary'
                              )}
                              onClick={() => {
                                setSelectedSiteMapPolygon(polygon);
                                setSelectedBlock(polygon.block);
                                setSelectedPolygonNumber(polygon.polygon_number);
                              }}
                            >
                              <div className="flex items-center gap-2 flex-1 min-w-0">
                                <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
                                <span className="text-sm font-medium truncate">
                                  {polygon.block}-{polygon.polygon_number}
                                </span>
                                {pointCount > 0 && (
                                  <Badge variant="outline" className="text-xs px-1.5 py-0 shrink-0">
                                    {pointCount} pts
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-0.5 shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newHidden = new Set(hiddenPolygons);
                                    if (newHidden.has(polygonKey)) {
                                      newHidden.delete(polygonKey);
                                    } else {
                                      newHidden.add(polygonKey);
                                    }
                                    setHiddenPolygons(newHidden);
                                  }}
                                  title={hiddenPolygons.has(polygonKey) ? "Show polygon" : "Hide polygon"}
                                >
                                  {hiddenPolygons.has(polygonKey) ? (
                                    <EyeOff className="h-3.5 w-3.5" />
                                  ) : (
                                    <Eye className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (confirm(`Delete polygon ${polygon.block}-${polygon.polygon_number}?`)) {
                                      const result = await deleteSiteMapPolygon(polygon.id);
                                      if (result.success) {
                                        await loadSiteMapPolygons();
                                        if (selectedSiteMapPolygon?.id === polygon.id) {
                                          setSelectedSiteMapPolygon(null);
                                          setCurrentPolygon([]);
                                          setHistory([]);
                                          setHistoryIndex(-1);
                                        }
                                      } else {
                                        toast({
                                          title: 'Delete Failed',
                                          description: result.error || 'Unknown error',
                                          variant: 'destructive',
                                        });
                                      }
                                    }
                                  }}
                                  title="Delete polygon"
                                >
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              </div>
                            </div>
                          );
                        })
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            <CoordinatePanel onImportComplete={loadExistingPolygons} />
          </div>

          {/* Main Canvas Area */}
          <div className="lg:col-span-3 space-y-4 order-1 lg:order-2">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-lg sm:text-xl truncate">
                      {mode === 'create-polygon'
                        ? selectedSiteMapPolygon
                          ? `Editing: ${selectedSiteMapPolygon.block}-${selectedSiteMapPolygon.polygon_number}`
                          : `Creating: ${selectedBlock}-${selectedPolygonNumber}`
                        : selectedUnit
                        ? `Tracing: ${selectedUnit}${selectedUnitFloor ? ` (${selectedUnitFloor})` : ''}`
                        : 'Select a unit to start'}
                    </CardTitle>
                    <CardDescription className="text-xs sm:text-sm">
                      {currentPolygon.length > 0
                        ? `${currentPolygon.length} point${currentPolygon.length !== 1 ? 's' : ''} - Click to add more`
                        : 'Click on the site plan to add polygon points'}
                    </CardDescription>
                  </div>
                  {mode === 'create-polygon' && (
                    <Button
                      onClick={handleSave}
                      disabled={currentPolygon.length < 3 || saving}
                      className="w-full sm:w-auto shrink-0"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          <span className="hidden sm:inline">Save Polygon</span>
                          <span className="sm:hidden">Save</span>
                        </>
                      )}
                    </Button>
                  )}
                  {mode === 'assign-units' && (
                    <Button
                      onClick={handleSave}
                      disabled={!selectedUnit || currentPolygon.length < 3 || saving}
                      className="w-full sm:w-auto shrink-0"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          <span className="hidden sm:inline">Save Polygon</span>
                          <span className="sm:hidden">Save</span>
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <TracingControls
                  onZoomIn={handleZoomIn}
                  onZoomOut={handleZoomOut}
                  onZoomReset={handleZoomReset}
                  onUndo={handleUndo}
                  onRedo={handleRedo}
                  onClear={handleClear}
                  canUndo={historyIndex > 0}
                  canRedo={historyIndex < history.length - 1}
                  hasPoints={currentPolygon.length > 0}
                  zoom={zoom}
                  showGrid={showGrid}
                  onToggleGrid={() => setShowGrid(!showGrid)}
                  className="mb-4"
                />

                <div className="border rounded-lg overflow-hidden" style={{ height: 'min(600px, calc(100vh - 400px))', minHeight: '400px' }}>
                  <MapCanvas
                    imageUrl="/site-plan-raw.svg"
                    viewBox="0 0 1728 864"
                    currentPolygon={currentPolygon}
                    existingPolygons={existingPolygons}
                    selectedUnit={selectedUnit && selectedUnitFloor ? `${selectedUnit}-${selectedUnitFloor}` : null}
                    onPointAdd={handlePointAdd}
                    onPointDelete={handlePointDelete}
                    onPointMove={handlePointMove}
                    onPolygonMove={handlePolygonMove}
                    onPolygonMoveStart={handlePolygonMoveStart}
                    zoom={zoom}
                    pan={pan}
                    onPanChange={setPan}
                    hiddenPolygons={hiddenPolygons}
                    currentPolygonKey={mode === 'create-polygon' && selectedSiteMapPolygon 
                      ? `${selectedSiteMapPolygon.block}-${selectedSiteMapPolygon.polygon_number}` 
                      : (selectedUnit && selectedUnitFloor ? `${selectedUnit}-${selectedUnitFloor}` : null)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}

